import React from 'react';
import AnimatedSection from './AnimatedSection';
import SectionIntro from './SectionIntro';

const layers = [
  {
    label: 'Operate',
    title: 'Run the floor',
    summary: 'POS, floor and back-office workflows, preparation and dispatch, staff scheduling, and mobile tools.',
    capabilities: ['Point of Sale & Checkout', 'Preparation & Operational Workflows', 'Staff & Workforce Management', 'Reservations & Booking'],
  },
  {
    label: 'Sell',
    title: 'Reach every customer',
    summary: 'Storefronts, QR ordering, delivery partners, and loyalty — all selling from one catalogue.',
    capabilities: ['Online Ordering & E-Commerce', 'QR Menu & Self-Service', 'Delivery & Fulfilment', 'Loyalty & Customer Engagement'],
  },
  {
    label: 'Control',
    title: 'Protect margins',
    summary: 'Inventory, purchasing, accounting, and real-time controls that stop leakage before it spreads.',
    capabilities: ['Inventory & Stock Management', 'Procurement & Purchasing', 'Accounting & Financial Operations', 'Multi-Branch Management'],
  },
  {
    label: 'Understand',
    title: 'See the business clearly',
    summary: 'Business intelligence, customer systems, and AI that turn operations into decisions.',
    capabilities: ['Business Analytics & Dashboards', 'Customer Relationship Management', 'AI Automation & Assistants'],
  },
  {
    label: 'Build',
    title: 'Create what your business needs',
    summary: 'Custom enterprise software and industry-specific operating systems built to your model.',
    capabilities: ['Hospitality Operating Systems', 'Commerce Platforms', 'Custom Enterprise Software'],
  },
];

const WhatWeDo: React.FC = () => {
  return (
    <section id="solutions" className="section-pad relative bg-surface">
      <div className="max-w-7xl mx-auto">
        <SectionIntro
          label="What We Do"
          title="The operating layer behind hospitality and commerce."
          body="We connect the work happening on the floor, in the stockroom, in the storefront, at the back office, and across every branch into one intelligent system."
          className="mb-14 md:mb-20"
        />

        <div className="space-y-0 divide-y divide-surface-border">
          {layers.map((layer, i) => (
            <AnimatedSection key={layer.label} animation="slide-up" delay={i * 60}>
              <article className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-5 md:gap-10 py-8 md:py-10">
                <div>
                  <span className="text-xs uppercase tracking-[0.22em] text-primary-500">{layer.label}</span>
                  <p className="mt-2 font-display text-3xl font-semibold text-white/15">
                    {String(i + 1).padStart(2, '0')}
                  </p>
                </div>

                <div>
                  <h3 className="font-display text-2xl md:text-3xl font-semibold text-white mb-3">
                    {layer.title}
                  </h3>
                  <p className="text-muted leading-relaxed mb-6 max-w-2xl">{layer.summary}</p>

                  <div className="flex flex-wrap gap-x-5 gap-y-3">
                    {layer.capabilities.map((capability) => (
                      <span key={capability} className="inline-flex items-center gap-2 text-sm text-white/75">
                        <span className="w-1 h-1 rounded-full bg-primary-500" />
                        {capability}
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            </AnimatedSection>
          ))}
        </div>

        <AnimatedSection animation="fade-in" delay={200}>
          <div className="mt-10 pt-10 border-t border-surface-border">
            <p className="text-xs uppercase tracking-[0.22em] text-primary-500 mb-3">Outcome</p>
            <p className="font-display text-2xl md:text-3xl text-white font-semibold leading-tight max-w-3xl">
              Less manual work. Cleaner reporting. Better margins. A business that can scale without chaos.
            </p>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default WhatWeDo;
